import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

import FaturaPublicaView from '../components/pagamento-publico/FaturaPublicaView';
import MetodoPagamentoPublico from '../components/pagamento-publico/MetodoPagamentoPublico';
import ConfirmacaoPagamentoPublico from '../components/pagamento-publico/ConfirmacaoPagamentoPublico';
import pagamentoPublicoService from '../services/pagamentoPublicoService';
import { useNotificacao } from '../contexts/NotificacaoContext';

const steps = ['Detalhes da Fatura', 'Método de Pagamento', 'Confirmação'];

const PagamentoPublico = () => {
  const { prestacaoId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [activeStep, setActiveStep] = useState(0);
  const [fatura, setFatura] = useState(null);
  const [metodoPagamento, setMetodoPagamento] = useState(null);
  const [metodosDisponiveis, setMetodosDisponiveis] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagamentoCriado, setPagamentoCriado] = useState(null);
  const [statusPagamento, setStatusPagamento] = useState(null);
  const [cancelarVerificacao, setCancelarVerificacao] = useState(null);

  const { adicionarNotificacao } = useNotificacao();

  // Carrega dados iniciais da fatura
  useEffect(() => {
    const carregarFatura = async () => {
      try {
        setLoading(true);
        setError(null);

        // Carregar fatura e métodos de pagamento em paralelo
        const [faturaResponse, metodosResponse] = await Promise.all([
          pagamentoPublicoService.obterFaturaPublica(prestacaoId, token),
          pagamentoPublicoService.listarMetodosPagamento(prestacaoId, token)
        ]);

        setFatura(faturaResponse.data);
        setMetodosDisponiveis(metodosResponse.data);

      } catch (error) {
        console.error('Erro ao carregar fatura:', error);
        setError(error.message || 'Erro ao carregar dados da fatura');
      } finally {
        setLoading(false);
      }
    };

    if (prestacaoId && token) {
      carregarFatura();
    } else {
      setError('Token de acesso não fornecido');
      setLoading(false);
    }

    // Cleanup
    return () => {
      if (cancelarVerificacao) {
        cancelarVerificacao();
      }
    };
  }, [prestacaoId, token, cancelarVerificacao]);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleMetodoPagamentoChange = (dados) => {
    setMetodoPagamento(dados);
    setError(null);
  };

  const handleConfirmarPagamento = async () => {
    if (!metodoPagamento || !fatura?.data) {
      setError('Dados de pagamento incompletos');
      return;
    }
  
    try {
      setLoading(true);
      setError(null);
      setStatusPagamento('PROCESSANDO');
  
      const dadosPagamento = {
        metodoPagamento: metodoPagamento.metodoPagamento,
        dadosPagamentoJSON: metodoPagamento.dadosPagamento,
        valorPago: Number(fatura.data.pagamento.valorTotal)
      };
  
      console.log('Enviando pagamento:', dadosPagamento);
  
      // Validar dados antes de enviar
      pagamentoPublicoService.validarDadosPagamento(dadosPagamento);
  
      const resultado = await pagamentoPublicoService.processarPagamento(
        prestacaoId,
        token,
        dadosPagamento
      );
  
      if (!resultado?.data?.pagamentoId) {
        throw new Error('Resposta inválida do servidor');
      }
  
      // Atualizar estado com resultado do pagamento
      setPagamentoCriado(resultado.data);
  
      // Atualizar estado da fatura localmente para evitar reload
      setFatura(prev => ({
        ...prev,
        data: {
          ...prev.data,
          prestacao: {
            ...prev.data.prestacao,
            status: resultado.data.status || prev.data.prestacao.status
          }
        }
      }));
  
      // Iniciar verificação periódica
      const cancelar = pagamentoPublicoService.iniciarVerificacaoPeriodica(
        resultado.data.pagamentoId,
        {
          onSuccess: (response) => {
            setStatusPagamento('CONFIRMADO');
            adicionarNotificacao('Pagamento confirmado com sucesso!', 'success');
            setLoading(false);
          },
          onError: (error) => {
            console.error('Erro no pagamento:', error);
            setStatusPagamento('ERRO');
            setError(error.message || 'Erro ao processar pagamento');
            adicionarNotificacao(
              `Erro no processamento do pagamento: ${error.message}`,
              'error'
            );
            setLoading(false);
          },
          onStatusChange: (novoStatus) => {
            setStatusPagamento(novoStatus);
            if (novoStatus === 'PENDENTE') {
              adicionarNotificacao(
                'Processando seu pagamento. Por favor, aguarde...',
                'info'
              );
            }
          },
          onTimeout: () => {
            setStatusPagamento('PENDENTE');
            adicionarNotificacao(
              'O pagamento está pendente. Você receberá um email quando for confirmado.',
              'warning'
            );
            setLoading(false);
          }
        },
        {
          intervalo: 10000, // 10 segundos
          maxTentativas: 12  // 2 minutos total
        }
      );
  
      setCancelarVerificacao(() => cancelar);
      handleNext(); // Avança para próxima etapa
  
    } catch (error) {
      console.error('Erro ao realizar pagamento:', error);
      setError(error.message || 'Erro ao realizar pagamento');
      setStatusPagamento('ERRO');
      adicionarNotificacao(
        `Erro no pagamento: ${error.message || 'Falha ao processar pagamento'}`,
        'error'
      );
      setLoading(false);
    }
  };
   
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <FaturaPublicaView 
            fatura={fatura} 
            error={error}
            onClose={() => setActiveStep(0)}
            onPagamento={() => handleNext()}
          />
        );
      
      case 1:
        return (
          <MetodoPagamentoPublico
            valorTotal={fatura?.valorTotal}
            valorOriginal={fatura?.valor}
            valorMulta={fatura?.valorMulta}
            metodosDisponiveis={metodosDisponiveis}
            onMetodoPagamentoChange={handleMetodoPagamentoChange}
            error={error}
          />
        );
      
      case 2:
        return (
          <ConfirmacaoPagamentoPublico
            open={true}
            onClose={() => setActiveStep(0)}
            pagamento={{
              ...pagamentoCriado,
              prestacao: fatura,
              metodoPagamento: metodoPagamento?.metodoPagamento,
              dadosPagamentoJSON: metodoPagamento?.dadosPagamento,
              status: statusPagamento
            }}
            loading={loading}
            error={error}
            success={statusPagamento === 'CONFIRMADO'}
            onConfirmar={handleConfirmarPagamento}
          />
        );
      
      default:
        return null;
    }
  };

  if (loading && activeStep === 0) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box mb={4}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => window.location.href = process.env.FRONTEND_URL}
        >
          Voltar ao Site
        </Button>
      </Box>

      <Box sx={{ width: '100%' }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && activeStep === 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 4 }}>
          {renderStepContent(activeStep)}
        </Box>

        {!error && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
            <Button
              color="inherit"
              disabled={activeStep === 0 || loading}
              onClick={handleBack}
            >
              Voltar
            </Button>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={
                    loading || 
                    (activeStep === 1 && !metodoPagamento)
                  }
                >
                  Próximo
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleConfirmarPagamento}
                  disabled={loading || !metodoPagamento || statusPagamento === 'CONFIRMADO'}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                      Processando...
                    </>
                  ) : (
                    'Confirmar Pagamento'
                  )}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Seção de Ajuda */}
      <Box mt={6} pt={3} borderTop={1} borderColor="divider">
        <Typography variant="h6" gutterBottom>
          Precisa de Ajuda?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Se você tiver alguma dúvida sobre o pagamento ou encontrar algum problema, 
          entre em contato com nosso suporte:
        </Typography>
        <Box mt={2}>
          <Typography variant="body2">
            • Email: suporte@zenkroll.co.mz
          </Typography>
          <Typography variant="body2">
            • Telefone: (+258) 84 635 1111 | 86 635 1111
          </Typography>
          <Typography variant="body2">
            • Horário de Atendimento: Segunda a Sexta, 8h às 17h
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default PagamentoPublico;
